import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { colors } from '../../../utils/theme';
import RevealOnScroll from '../../../utils/RevealOnScroll';
import arcteryxLogo from '../../../images/FeaturedProjects/Arcteryx/Arcteryx.png';
import { BsChevronDown } from 'react-icons/bs';

const ProjectContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background-color: ${colors.backgroundColor};
  color: ${colors.textColor};
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6rem 0;
  text-align: center;
`;

const Logo = styled.img`
  width: 400px;
  height: auto;

  
  @media (max-width: 768px) {
    width: 300px;
  }
`;

const Subtitle = styled.h2`
  font-size: clamp(1.2rem, 2vw, 1.5rem);
  color: ${colors.subtitleColor};
  margin-top: 0rem;
  font-family: "maitree", serif;
  max-width: 600px;
  text-align: left;
  font-weight: 400;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ContentSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Description = styled.p`
  font-size: 1.4rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: ${colors.subtitleColor};
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 2rem 0;
`;

const TechItem = styled(motion.div)`
  background-color: ${colors.accentColor};
  color: ${colors.backgroundColor};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const ImageSection = styled.div`
  width: 100%;
  margin: 4rem 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ArrowContainer = styled(motion.div)`
  margin-top: 2rem;
  color: white;
  opacity: 0.3;
  font-size: 1.5rem;
`;

const InstagramFeed = styled.div`
  width: 100%;
  padding: 4rem 0;
  background-color: ${colors.backgroundColor};
  overflow: hidden;
`;

const InstagramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const InstagramPost = styled.div`
  width: 100%;
  min-height: 400px;
  background: ${colors.backgroundColor};
  border-radius: 8px;
  overflow: hidden;

  iframe {
    width: 100% !important;
    margin: 0 !important;
  }
`;

const Arcteryx = () => {
  const techStack = [
    'Adobe Premiere Pro',
    'Adobe After Effects',
    'Adobe Photoshop',
    'Content Creation',
    'Event Photography',
    'Social Media Management'
  ];

  const arrowVariants = {
    animate: {
      y: [0, 10, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const instagramPosts = [
    "https://www.instagram.com/p/DFrRChPur1N/",
    'https://www.instagram.com/p/DFYOLVaOWOq/?utm_source=ig_embed&amp;utm_campaign=loading',
    'https://www.instagram.com/reel/DCvjLWpOr8r/?utm_source=ig_embed&amp;utm_campaign=loading',
  ];

  useEffect(() => {
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    // Initialize embeds when script loads
    script.onload = () => {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []); // Initial script load

  // Re-process embeds when posts change
  useEffect(() => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, []);

  return (
    <ProjectContainer>
      <HeaderSection>
        <Logo src={arcteryxLogo} alt="Arc'teryx Logo" />
        <Subtitle>Arc'teryx fuels the stoke — sponsored content that gets people excited, drives engagement, and strengthens brand loyalty.</Subtitle>
        <ArrowContainer
          variants={arrowVariants}
          animate="animate"
        >
          <BsChevronDown />
        </ArrowContainer>
      </HeaderSection>

      <InstagramFeed>
        <InstagramGrid>
          {instagramPosts.map((post, index) => (
            <RevealOnScroll key={index}>
              <InstagramPost>
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink={post}
                  data-instgrm-version="14"
                  style={{ 
                    background: '#FFF',
                    border: '0',
                    borderRadius: '3px',
                    boxShadow: 'none',
                    margin: '1px',
                    maxWidth: '540px',
                    minWidth: '326px',
                    padding: '0',
                    width: '99.375%'
                  }}
                />
              </InstagramPost>
            </RevealOnScroll>
          ))}
        </InstagramGrid>
      </InstagramFeed>

      <ContentSection>
        <RevealOnScroll>
          <Description>
          Executive-produced and edited sponsored content that drove engagement, built excitement, and strengthened our partnership. I ensured every piece aligned with Arc’teryx’s brand guidelines while capturing the energy of university life and modern content trends. Balancing authenticity with a polished, professional look, I helped craft content that resonated with students and elevated Arc’teryx’s presence in our community.          </Description>
        </RevealOnScroll>

      



      </ContentSection>
    </ProjectContainer>
  );
};

export default Arcteryx;
